import React from 'react';
import { Helmet } from 'react-helmet';
import seoSettings from '../data/SEOSettings.json';
import socialLinks from '../data/socialLinks.json';

const buildBreadcrumb = (baseUrl, canonical) => {
  try {
    if (!canonical) return null;
    const parts = canonical.split('/').filter(Boolean);
    const itemList = parts.map((p, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: decodeURIComponent(p.replace(/[-_]/g, ' ')),
      item: `${baseUrl}/${parts.slice(0, idx + 1).join('/')}`
    }));

    // root item
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: baseUrl
        },
        ...itemList
      ]
    };

    return breadcrumb;
  } catch (e) {
    return null;
  }
};

const SEO = ({ pageName, customData = {} }) => {
  const { defaultMeta } = seoSettings;
  const pageData = seoSettings.pages[pageName] || {};

  const title = customData.title || pageData.title || defaultMeta.siteName;
  const description = customData.description || pageData.description || '';
  const keywords = [...(pageData.keywords || []), ...(defaultMeta.globalKeywords || [])];
  const ogImage = (customData.ogImage || pageData.ogImage || defaultMeta.defaultImage) || '';
  const ogType = customData.ogType || pageData.ogType || defaultMeta.defaultType || 'website';
  const canonical = customData.canonical || pageData.canonical || '';

  const fullTitle = title ? `${title} | ${defaultMeta.siteName}` : defaultMeta.siteName;
  const baseUrl = defaultMeta.baseUrl || 'https://leadwithtech.in';
  const pageUrl = canonical ? `${baseUrl}${canonical}` : (typeof window !== 'undefined' ? `${baseUrl}${window.location.pathname}` : baseUrl);

  // Organization schema
  const sameAs = (socialLinks && socialLinks.links) ? socialLinks.links.map(s => s.url) : [];
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: defaultMeta.siteName,
    url: baseUrl,
    logo: `${baseUrl}${defaultMeta.defaultImage}`,
    sameAs
  };

  // WebSite schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: baseUrl,
    name: defaultMeta.siteName,
    description: defaultMeta.siteName,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  // Page specific schema
  let pageSchema = null;
  if (ogType === 'course') {
    pageSchema = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: title,
      description,
      provider: {
        '@type': 'Organization',
        name: defaultMeta.siteName,
        sameAs: baseUrl
      }
    };
  } else if (ogType === 'blog') {
    pageSchema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: title,
      description,
      url: pageUrl,
      publisher: {
        '@type': 'Organization',
        name: defaultMeta.siteName,
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}${defaultMeta.defaultImage}`
        }
      }
    };
  } else {
    pageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: pageUrl
    };
  }

  const breadcrumb = buildBreadcrumb(baseUrl, canonical);

  // Compose all schemas into an array (or single object if preferred)
  const structuredData = [organizationSchema, websiteSchema, pageSchema];
  if (breadcrumb) structuredData.push(breadcrumb);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${ogImage}`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${ogImage}`} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={`${baseUrl}${canonical}`} />}

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="UTF-8" />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

export default SEO;